/**
 * Browser-side awards data client.
 *
 * Loads a flat JSON snapshot of every award body, edition, and
 * nomination from /awards.json (served by the PWA host, cached by
 * the service worker after first fetch). All queries run against the
 * in-memory structure so the Awards tab works offline after one visit.
 */

export interface RawNomination {
  categoryId: string
  categoryName: string
  movieTmdbId: number | null
  movieTitle: string | null
  personTmdbId: number | null
  personName: string | null
  isWinner: boolean
}

export interface RawEdition {
  id: string
  name: string
  year: number
  date: string | null
  ordinal: string | null
  nominations: RawNomination[]
}

export interface RawBody {
  name: string
  country: string | null
  editions: RawEdition[]
}

export type AwardsIndex = Record<string, RawBody>

export interface AwardBodySummary {
  wikidataId: string
  name: string
  country: string | null
  editionCount: number
  latestYear: number | null
  nominationCount: number
  winCount: number
}

export interface MovieAwardRow {
  tmdbId: number
  title: string
  wins: number
  nominations: number
}

export interface PersonAwardRow {
  name: string
  tmdbId: number | null
  wins: number
  nominations: number
}

let cachedPromise: Promise<AwardsIndex> | null = null

export function loadAwards(): Promise<AwardsIndex> {
  if (cachedPromise) return cachedPromise
  cachedPromise = fetch('/awards.json', { cache: 'force-cache' })
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to load awards.json: ${res.status}`)
      return res.json() as Promise<AwardsIndex>
    })
    .catch((err) => {
      cachedPromise = null
      throw err
    })
  return cachedPromise
}

export function summarizeBodies(index: AwardsIndex): AwardBodySummary[] {
  return Object.entries(index)
    .map(([wikidataId, body]) => {
      let nominationCount = 0
      let winCount = 0
      let latestYear: number | null = null
      for (const edition of body.editions) {
        if (latestYear == null || edition.year > latestYear) latestYear = edition.year
        for (const nom of edition.nominations) {
          nominationCount++
          if (nom.isWinner) winCount++
        }
      }
      return {
        wikidataId,
        name: body.name,
        country: body.country,
        editionCount: body.editions.length,
        latestYear,
        nominationCount,
        winCount,
      }
    })
    .sort((a, b) => b.nominationCount - a.nominationCount)
}

/**
 * Leaderboard: movies sorted by wins then nominations across every body.
 */
export function topMovies(index: AwardsIndex, limit = 40): MovieAwardRow[] {
  const map = new Map<number, MovieAwardRow>()
  for (const body of Object.values(index)) {
    for (const edition of body.editions) {
      for (const nom of edition.nominations) {
        if (nom.movieTmdbId == null) continue
        const existing = map.get(nom.movieTmdbId)
        if (existing) {
          existing.nominations++
          if (nom.isWinner) existing.wins++
        } else {
          map.set(nom.movieTmdbId, {
            tmdbId: nom.movieTmdbId,
            title: nom.movieTitle ?? `TMDB #${nom.movieTmdbId}`,
            wins: nom.isWinner ? 1 : 0,
            nominations: 1,
          })
        }
      }
    }
  }
  return [...map.values()]
    .sort((a, b) => (b.wins - a.wins) || (b.nominations - a.nominations))
    .slice(0, limit)
}

/**
 * Leaderboard: people (actors/directors/etc.) sorted by wins then nominations.
 * Names that the scraper packed with commas (producer credits) are split apart
 * so each individual gets credit. TMDB IDs are optional — most rows identify
 * by name only.
 */
export function topPersons(index: AwardsIndex, limit = 40): PersonAwardRow[] {
  const map = new Map<string, PersonAwardRow>()
  for (const body of Object.values(index)) {
    for (const edition of body.editions) {
      for (const nom of edition.nominations) {
        if (!nom.personName) continue
        const names = nom.personName.split(/\s*,\s*/).filter(Boolean)
        for (const name of names) {
          const key = name.toLowerCase()
          const existing = map.get(key)
          if (existing) {
            existing.nominations++
            if (nom.isWinner) existing.wins++
          } else {
            map.set(key, {
              name,
              tmdbId: nom.personTmdbId ?? null,
              wins: nom.isWinner ? 1 : 0,
              nominations: 1,
            })
          }
        }
      }
    }
  }
  return [...map.values()]
    .sort((a, b) => (b.wins - a.wins) || (b.nominations - a.nominations))
    .slice(0, limit)
}

/**
 * Awards for a single movie, grouped and sorted by body.
 *
 * Each entry carries `bodyWikidataId` and `editionId` so the compact
 * awards section in the detail modal can deep-link straight into the
 * matching Awards tab → body → ceremony without another lookup.
 */
export interface MovieAwardEntry {
  bodyName: string
  bodyWikidataId: string
  editionId: string
  year: number
  categoryName: string
  isWinner: boolean
  personName: string | null
}

export function awardsForMovie(
  index: AwardsIndex,
  tmdbId: number
): MovieAwardEntry[] {
  const out: MovieAwardEntry[] = []
  for (const [wikidataId, body] of Object.entries(index)) {
    for (const edition of body.editions) {
      for (const nom of edition.nominations) {
        if (nom.movieTmdbId === tmdbId) {
          out.push({
            bodyName: body.name,
            bodyWikidataId: wikidataId,
            editionId: edition.id,
            year: edition.year,
            categoryName: nom.categoryName,
            isWinner: nom.isWinner,
            personName: nom.personName,
          })
        }
      }
    }
  }
  return out.sort((a, b) => {
    // Wins float to the top, then newest year first — matches the
    // ordering the compact awards section in the detail modal expects.
    if (a.isWinner !== b.isWinner) return Number(b.isWinner) - Number(a.isWinner)
    if (a.year !== b.year) return b.year - a.year
    return a.bodyName.localeCompare(b.bodyName)
  })
}

/**
 * Every edition of a body, newest first.
 */
export function editionsForBody(index: AwardsIndex, wikidataId: string): RawEdition[] {
  const body = index[wikidataId]
  if (!body) return []
  return [...body.editions].sort((a, b) => b.year - a.year)
}

/**
 * Group an edition's nominations by category, with winner first in each group.
 */
export interface CategoryGroup {
  categoryId: string
  categoryName: string
  nominations: RawNomination[]
}

export function groupByCategory(edition: RawEdition): CategoryGroup[] {
  // Preserve the order in which categories first appear in the source
  // nominations list — the scraper keeps them in editorial order (Best
  // Picture, then acting, then craft, then specialty), which matches the
  // desktop and what users expect. Alphabetical sorting scrambled that.
  const groups = new Map<string, CategoryGroup>()
  for (const nom of edition.nominations) {
    let group = groups.get(nom.categoryId)
    if (!group) {
      group = {
        categoryId: nom.categoryId,
        categoryName: nom.categoryName,
        nominations: [],
      }
      groups.set(nom.categoryId, group)
    }
    group.nominations.push(nom)
  }
  // Sort nominations within each category: winners first
  for (const g of groups.values()) {
    g.nominations.sort((a, b) => Number(b.isWinner) - Number(a.isWinner))
  }
  return [...groups.values()]
}

/**
 * A category is "secondary" when it's a technical / craft / specialty
 * award that the desktop hides behind a "Show technical awards" toggle.
 * We mirror the heuristic here because the JSON snapshot does not carry
 * an `isSecondary` flag — the scraper only writes main/supporting award
 * rows with an editorial ordering and relies on this kind of keyword
 * match to split them.
 */
const SECONDARY_KEYWORDS = [
  'sound',
  'visual effect',
  'editing',
  'makeup',
  'costume',
  'production design',
  'art direction',
  'cinematograph',
  'score',
  'song',
  'short',
  'documentary',
  'animated feature',
  'foreign',
  'international',
  'technical',
  'hairstyling',
  'casting',
  'screenplay',
]

export function isSecondaryCategory(name: string): boolean {
  const lower = name.toLowerCase()
  // Preserve the "Big Five" main categories even if they contain
  // a keyword above (e.g. "Best Original Screenplay" -> secondary,
  // but "Best Picture"/"Best Director"/"Best Actor"/"Best Actress"
  // stay main).
  if (
    lower.includes('best picture') ||
    lower === 'best director' ||
    lower.includes('best actor') ||
    lower.includes('best actress') ||
    lower.includes('palme') ||
    lower.includes('golden lion') ||
    lower.includes('golden bear')
  ) {
    return false
  }
  return SECONDARY_KEYWORDS.some((k) => lower.includes(k))
}

export interface DecadeGroup {
  decade: string // e.g. "2020s"
  editions: RawEdition[]
}

/**
 * Desktop sidebar grouping: newest decade first, each decade's editions
 * sorted newest → oldest so the "current" year sits at the top.
 */
export function editionsByDecade(editions: RawEdition[]): DecadeGroup[] {
  const map = new Map<string, RawEdition[]>()
  for (const ed of editions) {
    const key = `${Math.floor(ed.year / 10) * 10}s`
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(ed)
  }
  const groups: DecadeGroup[] = []
  for (const [decade, eds] of map.entries()) {
    eds.sort((a, b) => b.year - a.year)
    groups.push({ decade, editions: eds })
  }
  groups.sort((a, b) => {
    const an = parseInt(a.decade, 10)
    const bn = parseInt(b.decade, 10)
    return bn - an
  })
  return groups
}

/**
 * A category is "person-centric" when every nomination in it names a
 * person (e.g. Best Actor) — the desktop uses this to decide whether to
 * render the person's photo rather than the film poster.
 */
export function isPersonCentricCategory(group: CategoryGroup): boolean {
  if (group.nominations.length === 0) return false
  return group.nominations.every(
    (n) => !!n.personName && !!n.movieTmdbId === false
      ? !!n.personName
      : !!n.personName
  )
}

/**
 * Case-insensitive search across every nomination by movie title. Returns
 * a compact row per (movie, ceremony) with every category the film was
 * cited in — exactly the data the desktop's "Search by film" panel shows.
 */
export interface FilmSearchHit {
  tmdbId: number | null
  title: string
  bodyName: string
  bodyWikidataId: string
  year: number
  editionId: string
  editionName: string
  categoryName: string
  isWinner: boolean
  personName: string | null
}

export function searchAwardsByFilm(
  index: AwardsIndex,
  query: string,
  limit = 200
): FilmSearchHit[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  const hits: FilmSearchHit[] = []
  for (const [wikidataId, body] of Object.entries(index)) {
    for (const edition of body.editions) {
      for (const nom of edition.nominations) {
        const title = nom.movieTitle?.toLowerCase() ?? ''
        if (!title.includes(q)) continue
        hits.push({
          tmdbId: nom.movieTmdbId,
          title: nom.movieTitle ?? `TMDB #${nom.movieTmdbId ?? '?'}`,
          bodyName: body.name,
          bodyWikidataId: wikidataId,
          year: edition.year,
          editionId: edition.id,
          editionName: edition.name,
          categoryName: nom.categoryName,
          isWinner: nom.isWinner,
          personName: nom.personName,
        })
        if (hits.length >= limit) return sortFilmHits(hits)
      }
    }
  }
  return sortFilmHits(hits)
}

function sortFilmHits(hits: FilmSearchHit[]): FilmSearchHit[] {
  return hits.sort((a, b) => {
    if (a.title !== b.title) return a.title.localeCompare(b.title)
    if (a.year !== b.year) return b.year - a.year
    if (a.bodyName !== b.bodyName) return a.bodyName.localeCompare(b.bodyName)
    return Number(b.isWinner) - Number(a.isWinner)
  })
}

/**
 * Aggregate per-film summary for the "Search by film" header card — one
 * row per unique tmdbId in the result set with total wins / nominations.
 */
export interface FilmSearchSummary {
  tmdbId: number | null
  title: string
  wins: number
  nominations: number
}

export function summarizeFilmHits(hits: FilmSearchHit[]): FilmSearchSummary[] {
  const map = new Map<string, FilmSearchSummary>()
  for (const h of hits) {
    const key = h.tmdbId != null ? `id:${h.tmdbId}` : `name:${h.title.toLowerCase()}`
    const existing = map.get(key)
    if (existing) {
      existing.nominations++
      if (h.isWinner) existing.wins++
    } else {
      map.set(key, {
        tmdbId: h.tmdbId,
        title: h.title,
        wins: h.isWinner ? 1 : 0,
        nominations: 1,
      })
    }
  }
  return [...map.values()].sort(
    (a, b) => b.wins - a.wins || b.nominations - a.nominations
  )
}
